import { zodResolver } from '@hookform/resolvers/zod'
import { X } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { DateQuickSelect } from '@/components/ui/date-quick-select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreateProject,
  useUpdateProject,
  type Project,
  type CreateProjectInput,
  type ProjectStatus,
} from '@/hooks'
import { useTeams } from '@/hooks'
import { cn } from '@/lib/utils'

interface ProjectFormProps {
  project?: Project
  teamId?: string // 预设的团队ID（从团队页面创建时传入）
  onClose: () => void
  onSuccess?: () => void
}

const STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: 'planning', label: '规划中' },
  { value: 'active', label: '进行中' },
  { value: 'completed', label: '已完成' },
]

const formSchema = z.object({
  name: z.string().min(1, '请输入项目名称').max(100, '项目名称不能超过100个字符'),
  description: z.string().max(2000, '项目描述不能超过2000个字符').optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  status: z.enum(['planning', 'active', 'completed', 'archived'] as const),
  teamId: z.string().min(1, '请选择所属团队'),
}).refine((data) => {
  if (data.startDate && data.endDate) {
    return data.startDate <= data.endDate
  }
  return true
}, {
  message: "截止日期不能早于开始日期",
  path: ["endDate"],
})

type FormValues = z.infer<typeof formSchema>

/**
 * 项目表单组件（创建/编辑）
 */
export function ProjectForm({ project, teamId: presetTeamId, onClose, onSuccess }: ProjectFormProps) {
  const isEdit = !!project
  const createMutation = useCreateProject()
  const updateMutation = useUpdateProject()
  const { data: teams } = useTeams({ status: 'active' })

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: project?.name || '',
      description: project?.description || '',
      startDate: project?.startDate ? project.startDate.split('T')[0] : '',
      endDate: project?.endDate ? project.endDate.split('T')[0] : '',
      status: (project?.status || 'planning') as FormValues['status'],
      teamId: project?.teamId || presetTeamId || '',
    },
  })

  // 监听团队预设变化
  if (presetTeamId && form.getValues('teamId') !== presetTeamId && !isEdit) {
    form.setValue('teamId', presetTeamId)
  }

  const onSubmit = (values: FormValues) => {
    if (isEdit) {
      updateMutation.mutate(
        {
          id: project.id,
          data: {
            name: values.name,
            description: values.description || undefined,
            startDate: values.startDate ? `${values.startDate}T00:00:00Z` : undefined,
            endDate: values.endDate ? `${values.endDate}T23:59:59Z` : undefined,
            status: values.status,
          },
        },
        {
          onSuccess: () => {
            onSuccess?.()
            onClose()
          },
        }
      )
    } else {
      const data: CreateProjectInput = {
        name: values.name,
        description: values.description || undefined,
        startDate: values.startDate ? `${values.startDate}T00:00:00Z` : undefined,
        endDate: values.endDate ? `${values.endDate}T23:59:59Z` : undefined,
        teamId: values.teamId,
      }
      createMutation.mutate(data, {
        onSuccess: () => {
          onSuccess?.()
          onClose()
        },
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending
  const mutationError = createMutation.error || updateMutation.error

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            {isEdit ? '编辑项目' : '新建项目'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="p-6">
          <div className="space-y-4">
            {/* 项目名称 */}
            <div className="space-y-2">
              <Label htmlFor="name">
                项目名称 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="请输入项目名称"
                {...form.register('name')}
                className={cn(form.formState.errors.name && 'border-destructive focus-visible:ring-destructive')}
              />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
              )}
            </div>

            {/* 所属团队（仅创建时显示） */}
            {!isEdit && (
              <div className="space-y-2">
                <Label htmlFor="teamId">
                  所属团队 <span className="text-destructive">*</span>
                </Label>
                <Controller
                  control={form.control}
                  name="teamId"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={!!presetTeamId}
                    >
                      <SelectTrigger className={cn(
                        'w-full',
                        form.formState.errors.teamId && 'border-destructive focus:ring-destructive',
                        presetTeamId && 'bg-muted'
                      )}>
                        <SelectValue placeholder="请选择团队" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams?.map((team) => (
                          <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {form.formState.errors.teamId && (
                  <p className="text-sm text-destructive">{form.formState.errors.teamId.message}</p>
                )}
                {presetTeamId && (
                  <p className="text-xs text-muted-foreground">从团队页面创建，团队已自动选择</p>
                )}
              </div>
            )}

            {/* 项目描述 */}
            <div className="space-y-2">
              <Label htmlFor="description">项目描述</Label>
              <Textarea
                id="description"
                placeholder="请输入项目描述"
                rows={3}
                {...form.register('description')}
                className={cn(form.formState.errors.description && 'border-destructive focus-visible:ring-destructive')}
              />
              {form.formState.errors.description && (
                <p className="text-sm text-destructive">{form.formState.errors.description.message}</p>
              )}
            </div>

            {/* 日期 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">开始日期</Label>
                <Input
                  id="startDate"
                  type="date"
                  {...form.register('startDate')}
                />
                <Controller
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <DateQuickSelect
                      value={field.value}
                      onChange={field.onChange}
                      options={{
                        includeToday: true,
                        includeTomorrow: true,
                        includeNextMonday: true,
                      }}
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">截止日期</Label>
                <Input
                  id="endDate"
                  type="date"
                  {...form.register('endDate')}
                  className={cn(form.formState.errors.endDate && 'border-destructive focus-visible:ring-destructive')}
                />
                <Controller
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <DateQuickSelect
                      value={field.value}
                      onChange={field.onChange}
                      options={{
                        includeToday: false,
                        includeTomorrow: true,
                        includeNextMonday: true,
                        includeThisWeekEnd: true,
                      }}
                    />
                  )}
                />
                {form.formState.errors.endDate && (
                  <p className="text-sm text-destructive">{form.formState.errors.endDate.message}</p>
                )}
              </div>
            </div>

            {/* 状态（仅编辑时显示） */}
            {isEdit && (
              <div className="space-y-2">
                <Label htmlFor="status">项目状态</Label>
                <Controller
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="选择状态" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}

            {/* 错误提示 */}
            {mutationError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {mutationError.message}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              取消
            </Button>
            <Button
              type="submit"
              disabled={isPending}
            >
              {isPending ? '保存中...' : isEdit ? '保存' : '创建'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
